<template>
  <div class="text-left pb-1">
    <p class="text-left text-2xl font-bold mb-12">{{ $t('address.add.title') }}</p>
    <div class="mb-6">
      <TextField :label="$t('address.add.addressLabel')" v-model="label" :placeholder="$t('address.add.addressLabelPlaceholder')" />
    </div>
    <div class="mb-6">
      <TextField :label="$t('address.add.senderName')" v-model="name" :placeholder="$t('address.add.senderNamePlaceholder')" />
    </div>
    <div class="mb-6">
      <TextField :label="$t('address.add.phone')" v-model="phoneNumber" :placeholder="$t('address.add.phonePlaceholder')" @keypress="numbersOnly" />
    </div>
    <div class="mb-6">
      <label class="text-sm text-neutral-700 font-medium mb-4 inline-block">{{ $t('address.add.selectIcon') }}</label>
      <div class="flex items-center gap-2 mdmax:flex-wrap">
        <div
          class="p-2 cursor-pointer rounded-md"
          :class="[selectedIcon === icon ? 'bg-primary-100' : 'bg-neutral-200']"
          @click="selectIcon(icon)"
          v-for="(icon, index) in iconsName"
          :key="'icon' + index"
        >
          <img :src="iconsMapping[icon]" class="h-6 w-6" />
        </div>
      </div>
    </div>
    <div class="mb-6 text-left relative">
      <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('address.add.searchAddress') }}</label>
      <div class="relative" id="city-search-container">
        <TextField withIcon type="text" @input="searchCity($event, 'destination')" @focus="visibleSuggestion = true" v-model="destinationSearch" :placeholder="$t('address.add.searchAddress')" />
        <div class="absolute left-3 top-1/2 transform -translate-y-1/2">
          <Search />
        </div>
        <img src="@/assets/images/rolling.gif" class="w-8 absolute top-1/2 transform -translate-y-1/2 right-2" v-if="isSearching" />
        <transition name="fade" mode="out-in">
          <div class="mt-1 z-50 border rounded-lg absolute top-full left-0 w-full bg-white" v-if="visibleSuggestion">
            <ul v-if="cities" class="max-h-40 overflow-y-scroll">
              <li class="cursor-pointer px-3 py-2 hover:bg-neutral-300 " v-for="(city, index) in cities" :key="'city-' + index" @click="selectCity(city, 'destination')">
                {{ city.village }}, {{ city.area_display }}
              </li>
            </ul>
            <div class="py-4 text-center" v-else>
              {{ $t('address.add.dataNotFound') }}
            </div>
          </div>
        </transition>
      </div>
    </div>
    <div class="mb-6">
      <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('address.add.mapLocation') }}</label>
      <div class="relative">
        <!-- Search Input -->
        <div class="absolute top-2 left-2 right-2 z-10">
          <div class="relative">
            <input
              type="text"
              v-model="placeSearch"
              @input="searchPlaces"
              @focus="showPlaceSuggestions = true"
              :placeholder="$t('address.add.searchLocation')"
              class="w-full px-4 py-2 pr-10 rounded-lg border border-neutral-300 bg-white shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div class="absolute right-3 top-1/2 transform -translate-y-1/2">
              <Search class="w-5 h-5 text-neutral-400" />
            </div>
            <!-- Autocomplete Suggestions -->
            <transition name="fade">
              <div
                v-if="showPlaceSuggestions && placeSuggestions.length > 0"
                class="absolute top-full mt-1 w-full bg-white rounded-lg shadow-lg border border-neutral-200 max-h-60 overflow-y-auto z-50"
              >
                <div
                  v-for="(place, index) in placeSuggestions"
                  :key="index"
                  @click="selectPlace(place)"
                  class="px-4 py-3 hover:bg-neutral-50 cursor-pointer border-b border-neutral-100 last:border-b-0"
                >
                  <div class="text-sm font-medium text-neutral-900">{{ place.description }}</div>
                </div>
              </div>
            </transition>
          </div>
        </div>
        <!-- Map -->
        <GmapMap
          :center="mapCenter"
          :zoom="mapZoom"
          map-type-id="roadmap"
          class="w-full h-64 rounded-lg overflow-hidden border border-neutral-300"
          :options="{
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true
          }"
          @click="updateMarkerPosition"
        >
          <GmapMarker :position="mapCenter" :draggable="true" @dragend="updateMarkerPosition" />
        </GmapMap>
        <!-- Pilih Lokasi Button -->
        <button
          @click="confirmLocation"
          :disabled="isConfirmingLocation"
          class="mt-3 w-full flex items-center justify-center gap-2 text-white px-4 py-2 rounded-lg shadow-lg font-medium transition-colors disabled:opacity-70"
          style="background-color: #61d7d0;"
        >
          <svg v-if="!isConfirmingLocation" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
          <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ isConfirmingLocation ? $t('address.add.loading') : $t('address.add.selectLocation') }}
        </button>
        <!-- Latitude and Longitude Display -->
        <div class="mt-2 text-sm text-neutral-600">
          <span class="font-medium">{{ $t('address.add.coordinates') }}:</span> {{ mapCenter.lat.toFixed(6) }}, {{ mapCenter.lng.toFixed(6) }}
        </div>
      </div>
    </div>
    <div class="mb-6">
      <Textarea :label="$t('address.add.address')" v-model="address" :placeholder="$t('address.add.addressPlaceholder')" />
    </div>
    <div class="mb-6">
      <Textarea :label="$t('address.add.detailAddress')" v-model="addressDetail" :placeholder="$t('address.add.detailAddressPlaceholder')" />
    </div>
    <div class="mb-12">
      <input type="checkbox" class="styled-checkbox" name="main-address" id="main-address" v-model="is_main" />
      <label for="main-address">{{ $t('address.add.mainAddress') }}</label>
    </div>
    <div class="flex items-center gap-3 mdmax:flex-col" :class="[isEditMode ? 'justify-between' : 'justify-end']">
      <div v-if="isEditMode" class="mdmax:w-full mdmax:order-2">
        <Button
          :buttonText="$t('address.add.delete')"
          :size="isMobile ? 'full' : 'regular'"
          type="delete_outline"
          @action="toggleConfirmModal('delete')"
          :icon="() => import('@/components/Icons/Trash')"
        />
      </div>
      <div class="flex items-center gap-3 justify-end mdmax:order-1 mdmax:w-full">
        <Button :buttonText="$t('address.add.cancel')" type="secondary" :size="isMobile ? 'full' : 'regular'" @action="closeModal()" />
        <Button :buttonText="$t('address.add.save')" :disabled="isDisabled" :size="isMobile ? 'full' : 'regular'" @action="save()" v-if="!isEditMode" />
        <Button :buttonText="$t('address.add.update')" :disabled="isDisabled" :size="isMobile ? 'full' : 'regular'" @action="toggleConfirmModal('save')" v-else />
      </div>
    </div>
    <Modal :modalVisible="visibleConfirmModal" @close="toggleConfirmModal()" width="600px" id="confirmation-modal">
      <template slot="modal-content">
        <div class="flex items-center justify-start gap-6 mb-6">
          <span class="flex-shrink-0">
            <Alert color="neutral-700" />
          </span>
          <div class="flex-col flex gap-2 items-start">
            <p class="text-2xl font-bold">{{ $t('address.add.confirmation') }}</p>
            <p class="text-sm text-neutral-500 text-left" v-if="confirmationAction === 'save'">
              {{ $t('address.add.confirmUpdate') }}
            </p>
            <p class="text-sm text-neutral-500 text-left" v-else>
              {{ $t('address.add.confirmDelete') }}
            </p>
          </div>
        </div>
        <div class="flex justify-end gap-4">
          <Button :buttonText="$t('address.add.no')" type="secondary" @action="toggleConfirmModal()" :size="isMobile ? 'full' : 'regular'" />
          <Button :buttonText="$t('address.add.yes')" type="primary" @action="save()" :size="isMobile ? 'full' : 'regular'" v-if="confirmationAction === 'save'" />
          <Button :buttonText="$t('address.add.yes')" type="primary" @action="deleteAddress()" :size="isMobile ? 'full' : 'regular'" v-else />
        </div>
      </template>
    </Modal>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {
    TextField: () => import('@/components/Forms/TextField'),
    Textarea: () => import('@/components/Forms/Textarea'),
    Button: () => import('@/components/Forms/Button'),
    Search: () => import('@/components/Icons/Search'),
    Modal: () => import('@/components/Modal/Modal'),
    Alert: () => import('@/components/Icons/Alert')
  },
  data() {
    return {
      phoneNumber: null,
      addressDetail: null,
      address: null,
      is_main: false,
      label: null,
      name: null,
      destinationSearch: '',
      cityKeyword: null,
      debounce: null,
      visibleSuggestion: false,
      isSearching: false,
      selectedIcon: null,
      originCity: null,
      mapCenter: {
        lat: -6.2,
        lng: 106.816666
      },
      mapZoom: 18,
      placeSearch: '',
      placeSuggestions: [],
      showPlaceSuggestions: false,
      placeSearchDebounce: null,
      iconsName: ['home', 'user', 'image', 'office', 'smile', 'star', 'heart', 'basket'],
      iconsMapping: {
        user: require('@/assets/images/icons/person.svg'),
        home: require('@/assets/images/icons/home.svg'),
        image: require('@/assets/images/icons/image.svg'),
        office: require('@/assets/images/icons/office.svg'),
        smile: require('@/assets/images/icons/smiled.svg'),
        star: require('@/assets/images/icons/star.svg'),
        heart: require('@/assets/images/icons/heart.svg'),
        basket: require('@/assets/images/icons/basket.svg')
      },
      visibleConfirmModal: false,
      confirmationAction: null,
      isConfirmingLocation: false
    }
  },
  computed: {
    ...mapGetters('master', ['cities']),
    ...mapGetters('common', ['isMobile']),
    ...mapGetters('auth', ['userData']),
    ...mapGetters('merchant', ['addressModalMode', 'selectedAddressId']),
    isDisabled() {
      return !this.originCity || !this.selectedIcon || !this.addressDetail || !this.name || !this.label || !this.phoneNumber
    },
    isEditMode() {
      return this.addressModalMode === 'EDIT'
    }
  },
  mounted() {
    this.name = this.userData?.user?.name
    if (this.isEditMode) {
      this.getAddressInformation()
    } else {
      this.getUserLocation()
    }
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    ...mapActions('master', ['getCities']),
    ...mapActions('merchant', ['addAddress', 'getAddressDetail', 'editAddress', 'removeAddress', 'geocodingReverse']),
    ...mapActions('common', ['showLoading', 'hideLoading', 'setToast']),
    deleteAddress() {
      this.showLoading()
      this.removeAddress({
        addressId: this.selectedAddressId
      }).then(() => {
        this.closeModal()
        this.hideLoading()
        this.setToast({
          options: { type: 'success', duration: 2000, primaryText: this.$t('address.add.toast.deleteSuccess'), secondaryText: this.$t('address.add.toast.dataSaved'), callback: () => {} }
        })
      })
    },
    toggleConfirmModal(action) {
      if (action) this.confirmationAction = action
      this.visibleConfirmModal = !this.visibleConfirmModal
    },
    getAddressInformation() {
      this.showLoading()
      this.getAddressDetail({
        addressId: this.selectedAddressId
      }).then(res => {
        this.label = res.location_label
        this.name = res.sender_name
        this.phoneNumber = res.sender_phone
        let addr = res.address || ''
        if (res.area) {
          const suffixFull = `, ${res.area.village}, ${res.area.area_display}`
          const suffixArea = `, ${res.area.area_display}`
          while (suffixFull && addr.endsWith(suffixFull)) {
            addr = addr.slice(0, -suffixFull.length)
          }
          while (suffixArea && addr.endsWith(suffixArea)) {
            addr = addr.slice(0, -suffixArea.length)
          }
        }
        this.address = addr
        this.addressDetail = res.detail_address
        this.originCity = res.area
        this.destinationSearch = res.area ? `${res.area.village}, ${res.area.area_display}` : ''
        this.is_main = res.is_main
        this.phoneNumber = this.normalizePhoneNumber(res.sender_phone || res.phone)

        // Load map coordinates if available
        if (res.location_coordinate && res.location_coordinate !== 'lat,long') {
          const coords = res.location_coordinate.split(',')
          if (coords.length === 2) {
            this.mapCenter = {
              lat: parseFloat(coords[0]),
              lng: parseFloat(coords[1])
            }
          }
        }

        this.hideLoading()
      })
    },
    closeModal() {
      this.resetForm()
      this.$emit('close')
    },
    resetForm() {
      this.phoneNumber = null
      this.addressDetail = null
      this.address = null
      this.is_main = false
      this.label = null
      this.name = this.userData?.user?.name
      this.destinationSearch = ''
      this.cityKeyword = null
      this.visibleSuggestion = false
      this.isSearching = false
      this.selectedIcon = null
      this.originCity = null
      this.mapCenter = {
        lat: -6.2,
        lng: 106.816666
      }
      this.mapZoom = 15
      this.placeSearch = ''
      this.placeSuggestions = []
      this.showPlaceSuggestions = false
      this.visibleConfirmModal = false
      this.confirmationAction = null
      this.isConfirmingLocation = false
    },
    selectIcon(icon) {
      this.selectedIcon = icon
    },
    getUserLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const lat = position.coords.latitude
            const lng = position.coords.longitude

            this.mapCenter = {
              lat: lat,
              lng: lng
            }

            // Get address from user's current location (don't auto-fill)
            this.getAddressFromCoordinates(lat, lng, false)
          },
          error => {
            console.error('Error getting location:', error)
            // Keep default Jakarta coordinates if geolocation fails
          }
        )
      }
    },
    updateMarkerPosition(event) {
      const newLat = event.latLng.lat()
      const newLng = event.latLng.lng()

      this.mapCenter = {
        lat: newLat,
        lng: newLng
      }
      this.showPlaceSuggestions = false
    },
    async getAddressFromCoordinates(lat, lng, shouldFillAddress = true) {
      try {
        const response = await this.geocodingReverse({
          payloads: {
            latitude: lat,
            longitude: lng
          }
        })

        if (response.data && response.data.data && response.data.data.formatted_address) {
          let formattedAddress = response.data.data.formatted_address

          // Remove Plus Code from the beginning (e.g., "RQFP+PPM " or "RQGQ+33Q, ")
          // Plus Codes are typically 4-8 characters followed by a plus sign and more characters, then a space or comma
          formattedAddress = formattedAddress.replace(/^[A-Z0-9]{4,8}\+[A-Z0-9]{2,3}[,\s]+/, '')

          // Fill address field based on shouldFillAddress parameter
          // When true (from Pilih Lokasi button), always fill the address
          // When false (from initial getUserLocation), don't auto-fill
          if (shouldFillAddress) {
            this.address = formattedAddress
            this.placeSearch = formattedAddress
          }
        } else {
          console.error('Geocoding failed:', response.data)
          throw new Error('Geocoding failed')
        }
      } catch (error) {
        console.error('Error fetching geocoding data:', error)
        throw error
      }
    },
    handleClickOutside(event) {
      const citySearchContainer = document.getElementById('city-search-container')
      const placeSearchContainer = event.target.closest('.relative')

      // Close city search if clicking outside of it
      if (citySearchContainer && !citySearchContainer.contains(event.target)) {
        this.visibleSuggestion = false
      }

      // Close place suggestions if clicking outside of search input
      if (!placeSearchContainer || !placeSearchContainer.querySelector('input[type="text"]')) {
        this.showPlaceSuggestions = false
      }
    },
    searchPlaces() {
      clearTimeout(this.placeSearchDebounce)

      if (!this.placeSearch || this.placeSearch.length < 3) {
        this.placeSuggestions = []
        return
      }

      this.placeSearchDebounce = setTimeout(() => {
        if (!window.google || !window.google.maps) {
          console.error('Google Maps not loaded')
          return
        }

        const service = new window.google.maps.places.AutocompleteService()
        service.getPlacePredictions(
          {
            input: this.placeSearch,
            componentRestrictions: { country: 'id' }
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              this.placeSuggestions = predictions
            } else {
              this.placeSuggestions = []
            }
          }
        )
      }, 300)
    },
    selectPlace(place) {
      this.placeSearch = place.description
      this.showPlaceSuggestions = false
      this.placeSuggestions = []

      // Get place details to retrieve coordinates
      if (window.google && window.google.maps) {
        const service = new window.google.maps.places.PlacesService(document.createElement('div'))
        service.getDetails(
          {
            placeId: place.place_id,
            fields: ['geometry']
          },
          (placeDetails, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && placeDetails.geometry) {
              // Update map center and marker position
              this.mapCenter = {
                lat: placeDetails.geometry.location.lat(),
                lng: placeDetails.geometry.location.lng()
              }
              // Optionally adjust zoom level
              this.mapZoom = 17
            }
          }
        )
      }
    },
    async confirmLocation() {
      // Close the place suggestions if open
      this.showPlaceSuggestions = false
      this.isConfirmingLocation = true

      try {
        // Trigger reverse geocoding to get address from coordinates
        await this.getAddressFromCoordinates(this.mapCenter.lat, this.mapCenter.lng, true)

        // Optionally show a toast or feedback to user
        this.setToast({
          options: {
            type: 'success',
            duration: 2000,
            primaryText: this.$t('address.add.toast.locationSelected'),
            secondaryText: `${this.$t('address.add.coordinates')}: ${this.mapCenter.lat.toFixed(6)}, ${this.mapCenter.lng.toFixed(6)}`,
            callback: () => {}
          }
        })
      } catch (error) {
        console.error('Error confirming location:', error)
        this.setToast({
          options: {
            type: 'error',
            duration: 2000,
            primaryText: this.$t('address.add.toast.locationFailed'),
            secondaryText: this.$t('address.add.toast.tryAgain'),
            callback: () => {}
          }
        })
      } finally {
        this.isConfirmingLocation = false
      }
    },
    searchCity(value) {
      this.cityKeyword = null
      if (value) this.isSearching = true
      this.visibleSuggestion = false
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        if (value) {
          this.cityKeyword = value
          this.getCities({
            params: {
              page: 1,
              per_page: 1000,
              search_keyword: this.cityKeyword
            }
          }).then(() => {
            this.visibleSuggestion = true
            this.isSearching = false
          })
        }
      }, 200)
    },
    selectCity(data) {
      this.originCity = data
      this.destinationSearch = `${data.village}, ${data.area_display}`
      this.visibleSuggestion = false
    },
    save() {
      this.visibleConfirmModal = false
      this.showLoading()
      const payloads = {
        location_label: this.label,
        location_coordinate: `${this.mapCenter.lat},${this.mapCenter.lng}`,
        asset_icon_id: this.selectedIcon,
        sender_name: this.name,
        sender_phone: this.normalizePhoneNumber(this.phoneNumber),
        address: this.address,
        detail_address: this.addressDetail,
        is_main: this.is_main,
        area_code: this.originCity.code
      }
      let self = this

      if (this.isEditMode) {
        this.editAddress({
          addressId: this.selectedAddressId,
          payloads: payloads
        }).then(() => {
          this.closeModal()
          this.hideLoading()
          this.$emit('refresh')
          this.setToast({
            options: { type: 'success', duration: 20000, primaryText: this.$t('address.add.toast.updateSuccess'), secondaryText: this.$t('address.add.toast.dataSaved'), callback: () => {} }
          })
        })
      } else {
        const mode = self.addressModalMode
        this.addAddress({
          payloads: payloads
        }).then(() => {
          this.closeModal()
          this.hideLoading()
          if (mode === 'RELOAD') {
            location.reload()
          }
          this.$emit('refresh')
          this.setToast({
            options: {
              type: 'success',
              duration: 2000,
              primaryText: this.$t('address.add.toast.addSuccess'),
              secondaryText: this.$t('address.add.toast.dataSaved'),
              callback: () => {}
            }
          })
        })
      }
    },
    numbersOnly(evt) {
      evt = evt ? evt : window.event
      var charCode = evt.which ? evt.which : evt.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        evt.preventDefault()
      } else {
        return true
      }
    },
    normalizePhoneNumber(phone) {
      if (!phone) return ''
      let digits = String(phone).replace(/\D/g, '')
      if (digits.startsWith('0062')) {
        digits = digits.slice(4)
        if (digits.startsWith('0')) digits = digits.slice(1)
        digits = '0' + digits
      } else if (digits.startsWith('620')) {
        digits = '0' + digits.slice(3)
      } else if (digits.startsWith('62')) {
        digits = '0' + digits.slice(2)
      } else if (digits.startsWith('8')) {
        digits = '0' + digits
      }
      return digits
    }
  }
}
</script>
<style lang="scss" scoped>
.styled-checkbox {
  position: absolute; // take it out of document flow
  opacity: 0; // hide it

  & + label {
    position: relative;
    cursor: pointer;
    padding: 0;
  }

  // Box.
  & + label:before {
    content: '';
    margin-right: 10px;
    display: inline-block;
    vertical-align: text-top;
    width: 20px;
    height: 20px;
    background: white;
    border: 1px solid #101840;
    border-radius: 4px;
  }

  // Box hover
  &:hover + label:before {
    background: #101840;
  }

  // Box focus
  &:focus + label:before {
    // box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.12);
  }

  // Box checked
  &:checked + label:before {
    background: #101840;
  }

  // Disabled state label.
  &:disabled + label {
    color: #b8b8b8;
    cursor: auto;
  }

  // Disabled box.
  &:disabled + label:before {
    box-shadow: none;
    background: #ddd;
  }

  // Checkmark. Could be replaced with an image
  &:checked + label:after {
    content: '';
    position: absolute;
    left: 5px;
    top: 9px;
    background: white;
    width: 2px;
    height: 2px;
    box-shadow: 2px 0 0 white, 4px 0 0 white, 4px -2px 0 white, 4px -4px 0 white, 4px -6px 0 white, 4px -8px 0 white;
    transform: rotate(45deg);
  }
}
</style>
